// @shared:standard — used by ConfigurationPage (admin) and ProjectSettingsModal (project).
// Impact: changes affect both global config and per-project config views.
import React from 'react';
import { GitBranch, Plus } from 'lucide-react';
import Toggle from './Toggle';
// Sorti dans un module .js dédié : un export non-composant depuis un .jsx casse
// le Fast Refresh (la zone disparaît de l'écran, sans trace au build). Les
// appelants passent par le baril `../shared`, qui le ré-exporte de là.
import { buildTaskTypesMap } from './taskTypesMap';

/**
 * Shared task-type configuration grid.
 *
 * Props:
 *   taskTypes        — raw backend items with data.workflow and data.parentId
 *                      (from taskTypeCollectionProcessor; includes both types and subtypes)
 *   enabledTypes     — { [typeName]: boolean }
 *   onChange(name)   — called when a type toggle is flipped
 *   onWorkflowClick(name, meta) — called when the GitBranch button is clicked;
 *                                 meta = { tid, workflowId, workflowStates, workflowTransitions }
 *   onAddSubtype(name, meta, subtypeNames) — OPTIONAL. When provided, each type card
 *                                 gets a "+" button to create a subtype under it.
 *                                 Omitted by read-only consumers (project settings).
 *   readOnly         — when true, toggles are disabled (pointer-events kept for workflow button)
 */
const TaskTypeConfigSection = ({
  taskTypes,
  enabledTypes = {},
  onChange,
  onWorkflowClick,
  onAddSubtype,
  readOnly = false,
}) => {
  const { map: taskTypesMap, meta: typeMeta, subtypeMeta } = buildTaskTypesMap(taskTypes);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {Object.keys(taskTypesMap).map(type => {
        const meta = typeMeta[type] ?? {};
        const hasWorkflow = !!meta.workflowId;
        const subtypes = taskTypesMap[type] ?? [];

        return (
          <div
            key={type}
            className="flex flex-col p-4 border border-slate-200 rounded-lg hover:border-iris-300 transition-colors"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-700">{type}</span>
                <button
                  type="button"
                  onClick={() => onWorkflowClick && onWorkflowClick(type, meta)}
                  aria-label={hasWorkflow ? `Voir le flux du type ${type}` : `Configurer un flux pour le type ${type}`}
                  title={hasWorkflow ? `Workflow : ${meta.workflowId}` : 'Aucun workflow — configurez-en un depuis la page de configuration'}
                  className={`p-1 rounded transition-colors ${
                    hasWorkflow
                      ? 'text-iris-600 hover:bg-iris-50'
                      : 'text-slate-300 hover:text-iris-400 hover:bg-iris-50'
                  }`}
                >
                  <GitBranch size={14} />
                </button>
                {onAddSubtype && !readOnly && (
                  <button
                    type="button"
                    onClick={() => onAddSubtype(type, meta, subtypes)}
                    aria-label={`Ajouter un sous-type au type ${type}`}
                    title={`Ajouter un sous-type à « ${type} »`}
                    className="p-1 rounded text-slate-400 transition-colors hover:bg-iris-50 hover:text-iris-600"
                  >
                    <Plus size={14} />
                  </button>
                )}
              </div>
              <Toggle
                checked={enabledTypes[type] ?? false}
                disabled={readOnly}
                label={`Activer le type ${type}`}
                onChange={() => onChange && onChange(type)}
              />
            </div>
            {subtypes.length > 0 && (
              <div className="text-xs text-slate-500 pl-2 border-l-2 border-slate-100 mt-1">
                <span className="font-semibold block mb-1">Sous-types :</span>
                <ul className="pl-2 space-y-1">
                  {subtypes.map(sub => {
                    const subMeta = subtypeMeta[sub] ?? {};
                    const subHasWorkflow = !!subMeta.workflowId;
                    return (
                      <li key={sub} className="flex items-center gap-2 text-sm">
                        <span className="w-1 h-1 bg-slate-400 rounded-full flex-shrink-0" />
                        <span className="flex-1">{sub}</span>
                        <button
                          type="button"
                          onClick={() => onWorkflowClick && onWorkflowClick(sub, subMeta)}
                          aria-label={subHasWorkflow ? `Voir le flux du sous-type ${sub}` : `Configurer un flux pour le sous-type ${sub}`}
                          title={subHasWorkflow ? `Workflow : ${subMeta.workflowId}` : 'Aucun workflow assigné'}
                          className={`p-0.5 rounded transition-colors ${
                            subHasWorkflow
                              ? 'text-iris-500 hover:bg-iris-50'
                              : 'text-slate-300 hover:text-iris-400 hover:bg-iris-50'
                          }`}
                        >
                          <GitBranch size={12} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TaskTypeConfigSection;
